import {
  ISetQuoteFieldStateMutationPayload,
  IQuoteSetStateActionPayload,
  IQuoteState
} from '@/store/quote';
import {
  IAddQuotePackageDocumentMutationPayload,
  IQuoteMutations
} from './mutations.types';
import {
  IQuoteData,
  IQuotePackageData,
  QuotePackageSectionEnum
} from '@/models';
import { Transition } from '@/models/transition';

export const mutations: IQuoteMutations = {
  SET_QUOTES(state: IQuoteState, payload: IQuoteData[]): void {
    state.quotes = payload;
  },

  SET_COUNT(state: IQuoteState, payload: number): void {
    state.count = payload;
  },

  INCREMENT_COUNT(state: IQuoteState): void {
    state.count += 1;
  },

  DECREMENT_COUNT(state: IQuoteState): void {
    state.count -= 1;
  },

  ADD_QUOTE(state: IQuoteState, payload: IQuoteData): void {
    state.quotes.push(payload);
  },

  UPDATE_QUOTE(state: IQuoteState, payload: IQuoteData): void {
    const index = state.quotes.findIndex(
      (quote: IQuoteData) => quote.number === payload.number
    );

    state.quotes.splice(index, 1, payload);
  },

  UPSERT_QUOTE(state: IQuoteState, payload: IQuoteData): void {
    const index = state.quotes.findIndex(
      (v: IQuoteData) => v.number === payload.number
    );
    if (index === -1) {
      state.quotes.push(payload);
      state.count += 1;
    } else {
      state.quotes.splice(index, 1, payload);
    }
  },

  SET_QUOTE_STATE(
    state: IQuoteState,
    payload: IQuoteSetStateActionPayload
  ): void {
    const index = state.quotes.findIndex(
      (quote: IQuoteData) => quote.number === payload.id
    );

    if (index != -1) {
      state.quotes[index].state = payload.state;
    }
  },

  REMOVE_QUOTE(state: IQuoteState, payload: number): void {
    const index = state.quotes.findIndex(
      (quote: IQuoteData) => quote.number === payload
    );

    state.quotes.splice(index, 1);
  },

  SET_QUOTE_ACTIVITIES: function(
    state: IQuoteState,
    payload: Transition[]
  ): void {
    state.quoteActivities = payload;
  },

  SET_LAST_PROMISE(state: IQuoteState, payload: Promise<any>): void {
    state.lastPromise = payload;
  },

  SET_QUOTE_QUOTE_PACKAGE(
    state: IQuoteState,
    payload: IQuotePackageData
  ): void {
    for (let index = 0; index < payload.quotes!.length; index++) {
      const packageQuote = payload.quotes![index];
      const quoteIndex = state.quotes.findIndex(
        (quote: IQuoteData) => quote.number === packageQuote.number
      );

      if (quoteIndex != -1) {
        const quote = state.quotes[quoteIndex];
        quote.package = payload.id;
        state.quotes.splice(quoteIndex, 1, packageQuote);
      }
    }
  },

  /**
   * Expects a generic type that is a key of the quote data. and updates the quote field depending on the field passed in
   * @param state
   * @param payload
   */
  SET_QUOTE_FIELD<T extends keyof IQuoteData>(
    state: IQuoteState,
    payload: ISetQuoteFieldStateMutationPayload<T>
  ) {
    const index = state.quotes.findIndex(
      (quote: IQuoteData) => quote.number === payload.id
    );

    if (index != -1) {
      state.quotes[index][payload.field] = payload.data;
    }
  },

  SET_QUOTE_PACKAGES(state: IQuoteState, payload: IQuotePackageData[]): void {
    state.quotePackages = payload;
  },

  ADD_QUOTE_PACKAGE(state: IQuoteState, payload: IQuotePackageData): void {
    state.quotePackages.push(payload);
  },

  UPDATE_QUOTE_PACKAGE(state: IQuoteState, payload: IQuotePackageData): void {
    const index = state.quotePackages.findIndex(
      (quotePackage: IQuotePackageData) => quotePackage.id === payload.id
    );

    state.quotePackages.splice(index, 1, payload);
  },

  /** add package document to specified order in quote package */
  ADD_QUOTE_PACKAGE_DOCUMENT(
    state: IQuoteState,
    payload: IAddQuotePackageDocumentMutationPayload
  ): void {
    const quotePackageIndex = state.quotePackages.findIndex(
      (quotePackage: IQuotePackageData) => quotePackage.id === payload.packageId
    );

    const documentIndex = state.quotePackages[
      quotePackageIndex
    ].package_documents!.findIndex(
      packageDocument =>
        packageDocument.section == payload.data.section &&
        packageDocument.order == payload.data.order! - 1
    );

    if (documentIndex == -1) {
      //if payload isn't found then we add it to the top of the list or the bottom depending on the section
      if (payload.data.section == QuotePackageSectionEnum.Cover) {
        state.quotePackages[quotePackageIndex].package_documents?.unshift(
          payload.data
        );
      } else {
        state.quotePackages[quotePackageIndex].package_documents?.push(
          payload.data
        );
      }
    } else {
      state.quotePackages[quotePackageIndex].package_documents?.splice(
        documentIndex,
        0,
        payload.data
      );
    }
  }
};
